import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  CategoryScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { HorseRead } from '../types/api';

interface HorseOwnersHistoryProps {
  horse: HorseRead;
}

interface OwnerEntry {
  name: string;
  startDate?: string;
  endDate?: string;
  isCurrent: boolean;
}

const DAY_MS = 1000 * 60 * 60 * 24;
const MIN_OWNER_DAY_GAP = 90;
const MAX_OWNER_DAY_GAP = 180;

const formatMonthYear = (value?: string) => {
  if (!value) return '';
  const [y, m] = value.split('-').map(Number);
  if (!y || !m) return value;
  const date = new Date(Date.UTC(y, m - 1, 1));
  return date.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const spreadOwnerDates = (dates: Date[], minGapDays: number, maxGapDays: number) => {
  const adjusted: Date[] = [];
  dates.forEach((raw, idx) => {
    if (idx === 0) {
      adjusted.push(raw);
      return;
    }
    const prev = adjusted[idx - 1];
    const minAllowed = prev.getTime() + minGapDays * DAY_MS;
    const maxAllowed = prev.getTime() + maxGapDays * DAY_MS;
    const clamped = Math.min(Math.max(raw.getTime(), minAllowed), maxAllowed);
    adjusted.push(new Date(clamped));
  });
  return adjusted;
};

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Tooltip, CategoryScale);

export const HorseOwnersHistory: React.FC<HorseOwnersHistoryProps> = ({ horse }) => {
  // Only show component if we have real owner history data
  if (!horse.unified_owner_history || horse.unified_owner_history.length === 0) {
    return null;
  }

  // Convert API format to component format
  const owners: OwnerEntry[] = horse.unified_owner_history.map((entry, idx, arr) => ({
    name: entry.owner,
    startDate: entry.min_update_time.split('T')[0].slice(0, 7), // Convert to YYYY-MM format
    endDate: entry.max_update_time.split('T')[0].slice(0, 7), // Convert to YYYY-MM format
    isCurrent: idx === arr.length - 1,
  }));

  // Build timeline points: each owner's start date + final owner's end date
  const rawDates: Date[] = [];
  const displayDates: Date[] = [];
  const ownerForSegment: string[] = [];

  owners.forEach((o, idx) => {
    const startDate = o.startDate ? new Date(o.startDate) : new Date();
    rawDates.push(startDate);
    displayDates.push(startDate);
    ownerForSegment.push(o.name);
    // Add end date as final circle for last owner
    if (idx === owners.length - 1 && o.endDate) {
      const endDate = new Date(o.endDate);
      rawDates.push(endDate);
      displayDates.push(endDate);
    }
  });

  // Spread dates for chart positioning
  const adjustedDates = spreadOwnerDates(rawDates, MIN_OWNER_DAY_GAP, MAX_OWNER_DAY_GAP);
  const hasLine = adjustedDates.length > 1;
  const lastIdx = adjustedDates.length - 1;

  const data = {
    datasets: [
      {
        label: 'Owners',
        data: adjustedDates.map((d, idx) => ({
          x: 0,
          y: d,
          displayDate: displayDates[idx],
          name: ownerForSegment[idx] || '',
          current: idx === lastIdx,
        })),
        borderColor: '#7DCD85',
        backgroundColor: '#7DCD85',
        pointBackgroundColor: '#FFFFFF',
        pointBorderColor: '#7DCD85',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 6,
        fill: false,
        tension: 0,
        showLine: hasLine,
        borderWidth: hasLine ? 2 : 0,
      },
    ],
  };

  const labelPlugin = {
    id: 'ownerLabels',
    afterDatasetsDraw: (chart: any) => {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const points = meta.data as any[];
      const dataArr = chart.data.datasets[0].data as any[];
      ctx.save();
      ctx.textAlign = 'left';
      ctx.fillStyle = '#7DCD85';
      ctx.font = '600 10px Inter, system-ui, -apple-system, sans-serif';
      const adjX = points[0]?.x + 12 || 12;

      // Draw date labels at each circle (using original dates)
      points.forEach((point: any, idx: number) => {
        const raw = dataArr[idx];
        const dateToShow = raw.displayDate || raw.y;
        const dateText = dateToShow ? formatMonthYear(dateToShow.toISOString().slice(0, 10)) : '';
        ctx.fillText(dateText, adjX, point.y + 4);
      });

      // Draw owner name labels at midpoints between consecutive circles
      for (let i = 0; i < points.length - 1; i++) {
        const currY = points[i].y;
        const nextY = points[i + 1].y;
        const midY = (currY + nextY) / 2;
        const name = ownerForSegment[i] || '';
        ctx.font = 'bold 10px Inter, system-ui, -apple-system, sans-serif';
        ctx.fillStyle = '#132B64';
        ctx.fillText(name, adjX, midY + 4);
        ctx.fillStyle = '#7DCD85';
        ctx.font = '10px Inter, system-ui, -apple-system, sans-serif';
      }
      ctx.restore();
    },
  };

  // Compute axis bounds
  const sortedDates = [...adjustedDates].sort((a, b) => a.getTime() - b.getTime());
  const minDate = sortedDates[0]
    ? new Date(sortedDates[0].getTime() - MIN_OWNER_DAY_GAP * DAY_MS)
    : undefined;
  const maxDate = sortedDates[sortedDates.length - 1]
    ? new Date(sortedDates[sortedDates.length - 1].getTime() + MIN_OWNER_DAY_GAP * DAY_MS)
    : undefined;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: () => '',
          label: (ctx: any) => ctx.raw?.name || '',
        },
      },
    },
    scales: {
      x: {
        type: 'linear' as const,
        display: false,
        grid: { display: false },
        border: { display: false },
        min: -1,
        max: 1,
      },
      y: {
        type: 'time' as const,
        time: { unit: 'month' as const, tooltipFormat: 'MMM yyyy', displayFormats: { month: 'MMM yy' } },
        grid: { display: false },
        border: { display: false },
        reverse: false,
        ticks: { display: false, padding: 6 },
        min: minDate,
        max: maxDate,
      },
    },
    elements: {
      line: { borderWidth: 2 },
      point: { hitRadius: 6 },
    },
    layout: {
      padding: { left: 0, right: 48, top: 16, bottom: 32 },
    },
  };

  const chartHeight = Math.max(150, 100 + adjustedDates.length * 80);

  return (
    <div className="mb-6 p-3">
      <div className="flex items-center justify-center mb-1">
        <h3 className="text-lg font-semibold text-gray-900 text-center">Owners History</h3>
      </div>

      <div className="px-0" style={{ height: chartHeight, marginLeft: '-20px' }}>
        <Line data={data} options={options as any} plugins={[labelPlugin]} />
      </div>
    </div>
  );
};
